import React, { type ReactNode } from 'react';

import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { adminMenuMeta } from '@components/admin/menuConfig';
import { operatorMenuMeta } from '@components/operator/menuConfig';

import PageHeader from './PageHeader';

const { Title } = Typography;

export type ListPageHeaderItem = string | { label: string; path?: string };

export interface ListPageHeaderProps {
  items?: ListPageHeaderItem[];
  basePath?: string;
  actions?: ReactNode;
  title?: string | ReactNode;
  hideTitle?: boolean;
  extra?: ReactNode;
  guideButton?: ReactNode;
}

/**
 * 列表頁標頭元件（麵包屑 + 標題 + 操作按鈕）
 *
 * 自動從麵包屑最後一個項目生成標題，適用於所有列表頁面。
 */
const ListPageHeader: React.FC<ListPageHeaderProps> = ({
  items = [],
  basePath = '/operator',
  actions,
  title: customTitle,
  hideTitle = false,
  extra,
  guideButton,
}) => {
  const { t } = useTranslation();

  // 根據 basePath 決定 i18n namespace 與 menuMeta
  const menuNamespace = basePath === '/admin' ? 'admin' : 'operator';
  const menuMeta = (basePath === '/admin' ? adminMenuMeta : operatorMenuMeta) as Record<string, { labelKey?: string }>;

  // 自動從 items 最後一項生成標題
  const getAutoTitle = (): string => {
    if (items.length === 0) return '';

    const lastItem = items[items.length - 1];

    if (typeof lastItem === 'string') {
      const labelKey = menuMeta[lastItem]?.labelKey;
      if (labelKey) return t(labelKey, { defaultValue: lastItem }) as string;
      return t(`${menuNamespace}.menu.${lastItem}.title`, {
        defaultValue: t(`${menuNamespace}.menu.${lastItem}`, { defaultValue: lastItem })
      }) as string;
    } else if (lastItem && lastItem.label) {
      return lastItem.label;
    }

    return '';
  };

  const title: string | ReactNode = customTitle ?? getAutoTitle();

  // 如果隱藏標題且沒有 actions，只顯示麵包屑
  if (hideTitle && !actions) {
    return <PageHeader items={items} basePath={basePath} extra={extra} guideButton={guideButton} />;
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <PageHeader items={items} basePath={basePath} extra={extra} guideButton={guideButton} />
      {!hideTitle && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Title level={4} style={{ margin: 0 }}>
            {title}
          </Title>
          {actions && (
            <div style={{ display: 'flex', gap: 8 }}>
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListPageHeader;
